import React, { useMemo, useState } from 'react';
import { Icon, getLocalDateAsString } from './LogisticsUtils';

const DatePickerModal = ({ show, onClose, onSelectDate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const today = useMemo(() => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    return base;
  }, []);

  if (!show) return null;

  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDayClick = (day) => {
    const selectedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );

    onSelectDate(getLocalDateAsString(selectedDate));
    onClose();
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const totalDays = daysInMonth(month, year);
    const startDay = firstDayOfMonth(month, year);

    const blanks = [];
    for (let i = 0; i < startDay; i += 1) {
      blanks.push(<div key={`blank-${i}`} className="h-12 rounded-2xl" />);
    }

    const days = [];
    for (let day = 1; day <= totalDays; day += 1) {
      const dayDate = new Date(year, month, day);
      dayDate.setHours(0, 0, 0, 0);

      const isPast = dayDate < today;
      const isToday = dayDate.getTime() === today.getTime();

      days.push(
        <button
          key={day}
          onClick={() => handleDayClick(day)}
          disabled={isPast}
          className={`flex h-12 items-center justify-center rounded-2xl text-sm font-semibold transition-all duration-300 ${
            isPast
              ? 'cursor-not-allowed bg-slate-100 text-slate-300'
              : isToday
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 hover:bg-blue-700'
              : 'bg-white text-slate-700 shadow-sm hover:bg-blue-50 hover:text-blue-700'
          }`}
        >
          {day}
        </button>
      );
    }

    return [...blanks, ...days];
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-200 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500">
                Programación
              </p>
              <h3 className="mt-2 text-2xl font-bold tracking-[-0.03em] text-slate-900">
                Seleccionar fecha
              </h3>
              <p className="mt-2 text-sm text-slate-500">
                Elegí el día en que querés dejar programada la hoja de ruta.
              </p>
            </div>

            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200"
            >
              <Icon path="M6 18L18 6M6 6l12 12" className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="mb-5 flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
            <button
              onClick={handlePrevMonth}
              className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition hover:bg-white"
            >
              <Icon path="M15.75 19.5L8.25 12l7.5-7.5" className="h-5 w-5" />
            </button>

            <h3 className="text-lg font-bold capitalize text-slate-900">
              {currentDate.toLocaleDateString('es-AR', {
                month: 'long',
                year: 'numeric'
              })}
            </h3>

            <button
              onClick={handleNextMonth}
              className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition hover:bg-white"
            >
              <Icon path="M8.25 4.5l7.5 7.5-7.5 7.5" className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-2 grid grid-cols-7 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">
            {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
              <div key={day} className="p-2">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">{renderCalendar()}</div>

          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
            Solo se pueden seleccionar fechas disponibles desde hoy en adelante.
          </div>
        </div>

        <div className="flex justify-end border-t border-slate-200 px-6 py-5">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-200"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatePickerModal;